import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormGroup,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Checkbox,
  Tooltip,
  Collapse,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import DeselectIcon from "@mui/icons-material/Deselect";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Swal from "sweetalert2";
import { getRequest, postRequest, patchRequest } from "Includes/api/apicall";
import { GET_URL, POST_URL, PATCH_URL } from "Includes/urls";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// Standard modules with all possible matching keys in report data
const STANDARD_MODULES = [
  {
    key: "finance",
    label: "Finance",
    reportKeys: [
      "fee_collection_last_used_value",
      "fee_plan_done",
      "finance_used_percentage",
      "finance",
      "fee_collection",
      "fees",
    ],
  },
  {
    key: "expense",
    label: "Expense",
    reportKeys: [
      "expenses_last_marked_value",
      "expense_used_percentage",
      "expense",
      "expenses",
    ],
  },
  {
    key: "payroll",
    label: "Payroll",
    reportKeys: [
      "payroll_used_percentage",
      "payroll",
      "staff_activity_status",
      "last_staff_logged_in",
      "number_of_staff_login",
      "staff_login_percentage",
    ],
  },
  {
    key: "attendance",
    label: "Attendance",
    reportKeys: [
      "attendance_used_percentage",
      "staff_attendance_last_marked_value",
      "student_attendance_last_used_value",
      "attendance",
    ],
  },
  {
    key: "exam",
    label: "Exam",
    reportKeys: [
      "last_exam_used_percentage",
      "exam_marks_used_percentage",
      "exam_marks_last_used_value",
      "exam_schedule_last_used_value",
      "exam",
      "examination",
    ],
  },
  {
    key: "notification",
    label: "Notification",
    reportKeys: [
      "bulknotification_last_marked_value",
      "notification_used_percentage",
      "notification",
      "notifications",
    ],
  },
  {
    key: "chat",
    label: "Chat",
    reportKeys: [
      "chat_used_percentage",
      "chat",
      "messages",
    ],
  },
  {
    key: "campus",
    label: "Campus",
    reportKeys: [
      "item_sold_last_marked_value",
      "miscellaneous_last_marked_value",
      "campus_used_percentage",
      "campus",
      "inventory",
      "store",
    ],
  },
  {
    key: "diary",
    label: "Diary",
    reportKeys: [
      "student_diary_last_used_value",
      "diary_used_percentage",
      "diary",
      "student_diary",
    ],
  },
  {
    key: "timetable",
    label: "Timetable",
    reportKeys: [
      "timetable_marked_value",
      "timetable",
    ],
  },
  {
    key: "admission",
    label: "Admission & Enquiry",
    reportKeys: [
      "admission_form_used",
      "admission_form_last_used",
      "enquiry_form_used",
      "enquiry_form_last_used",
      "application_form_used",
      "application_form_last_used",
      "last_student_form_entered",
    ],
  },
  {
    key: "student_portal",
    label: "Student / Parent Portal",
    reportKeys: [
      "student_activity_status",
      "number_of_student_login",
      "student_login_percentage",
    ],
  },
  // Newly added module features
  {
    key: "gatepass",
    label: "Gatepass",
    reportKeys: [
      "gatepass_used",
      "gatepass",
      "gate_pass",
      "gate_pass_used",
    ],
  },
  {
    key: "visitor",
    label: "Visitor Management",
    reportKeys: [
      "visitor_used",
      "visitor",
      "visitors",
      "visitor_management",
    ],
  },
  {
    key: "complaint",
    label: "Complaint System",
    reportKeys: [
      "complaint_used",
      "complaint",
      "complaints",
      "complaint_system",
    ],
  },
  {
    key: "certificate",
    label: "Certificate",
    reportKeys: [
      "certificate_used",
      "certificate",
      "certificates",
      "study_certificate",
    ],
  },
  {
    key: "transport",
    label: "Transport",
    reportKeys: [
      "transport_used",
      "transport",
      "vehicles",
      "bus",
      "transport_management",
    ],
  },
];

/**
 * Check if a module is used (even if used at least once).
 * Returns { isUsed: boolean, details: string }
 */
function checkModuleUsage(reportRow, moduleFeature) {
  if (!reportRow) {
    return { isUsed: false, details: "Never Used" };
  }

  // Merge top-level reportRow and nested report_data
  const data = { ...(reportRow.report_data || {}), ...reportRow };

  for (const key of moduleFeature.reportKeys) {
    const rawVal = data[key];
    if (rawVal !== undefined && rawVal !== null && rawVal !== "") {
      const valStr = String(rawVal).trim();
      const lower = valStr.toLowerCase();

      // If value is not a negative indicator
      if (
        lower !== "never used" &&
        lower !== "no" &&
        lower !== "0" &&
        lower !== "0%" &&
        lower !== "0.0" &&
        lower !== "0.00%" &&
        lower !== "false"
      ) {
        // If numeric and greater than 0
        const num = parseFloat(valStr);
        if (!isNaN(num) && num > 0) {
          return { isUsed: true, details: `Used (${valStr})` };
        }
        // If affirmative string
        if (lower === "yes" || lower === "true") {
          return { isUsed: true, details: "Active" };
        }
        // If it's a date or status string
        if (lower !== "0") {
          return { isUsed: true, details: `Used on ${valStr}` };
        }
      }
    }
  }

  return { isUsed: false, details: "Never Used" };
}

/**
 * Safely load an image URL into a base64 data URI for jsPDF.
 * Always resolves to avoid rejecting and crashing PDF generation.
 */
function loadImageAsBase64(url) {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth || img.width || 100;
          canvas.height = img.naturalHeight || img.height || 100;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL("image/png");
          resolve(dataUrl);
        } catch (e) {
          console.warn("Canvas export error for logo:", e);
          resolve(null);
        }
      };
      img.onerror = () => {
        console.warn("Failed to load image from URL:", url);
        resolve(null);
      };
      img.src = url;
      setTimeout(() => resolve(null), 3000);
    } catch (e) {
      console.warn("Image load exception:", e);
      resolve(null);
    }
  });
}

function CompanyChecklistTab(props) {
  const { company_id, hideAssign } = props;
  const [checklistItems, setChecklistItems] = useState([]);
  const [companyItems, setCompanyItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [selectedModules, setSelectedModules] = useState(new Set());
  const [showUnused, setShowUnused] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // When company_id changes, reset all state to prevent stale data from previous school
  useEffect(() => {
    setCompanyInfo(null);
    setReportData(null);
    setChecklistItems([]);
    setCompanyItems([]);
    setSelectedModules(new Set());
    setShowUnused(false);

    if (company_id) {
      fetchData(company_id);
    }
  }, [company_id]);

  const fetchData = async (idToFetch) => {
    setLoading(true);
    try {
      // 1. Fetch company info directly by ID so the school name is ALWAYS accurate and fresh
      const companyRes = await getRequest(
        `${GET_URL.company.api}${idToFetch}/`,
        {},
        props
      );
      if (companyRes?.status === 200) {
        const compData =
          companyRes?.data?.data || companyRes?.data || null;
        setCompanyInfo(compData);
      }

      // 2. Fetch checklist items
      const itemsRes = await getRequest(GET_URL.checklist_items.api, {}, props);
      const compRes = await getRequest(
        `${GET_URL.company_checklist_items.api}?company_id=${idToFetch}`,
        {},
        props
      );

      const items = Array.isArray(itemsRes?.data)
        ? itemsRes.data
        : itemsRes?.data?.data || [];
      const compItems = Array.isArray(compRes?.data)
        ? compRes.data
        : compRes?.data?.data || [];

      setChecklistItems(items);
      setCompanyItems(compItems);

      // 3. Fetch client track report for this exact company
      const reportRes = await getRequest(
        GET_URL.client_track_report.api,
        { company: idToFetch, limit: 10 },
        props
      );
      if (reportRes?.status === 200) {
        const reportRows = reportRes?.data?.data || [];
        if (reportRows.length > 0) {
          setReportData(reportRows[0]);
        } else {
          setReportData(null);
        }
      } else {
        setReportData(null);
      }
    } catch (e) {
      console.error("Checklist fetchData error:", e);
    }
    setLoading(false);
  };

  // Filter unassigned checklist items for dropdown
  const unassignedItems = useMemo(() => {
    const assignedIds = new Set(companyItems.map((ci) => ci.checklist_item));
    return checklistItems.filter((item) => !assignedIds.has(item.id));
  }, [checklistItems, companyItems]);

  // Compute full module usage list for current school
  const moduleUsage = useMemo(() => {
    return STANDARD_MODULES.map((mod) => {
      const usage = checkModuleUsage(reportData, mod);
      return {
        ...mod,
        isUsed: usage.isUsed,
        details: usage.details,
      };
    });
  }, [reportData]);

  // Partition modules into Used and Unused
  const usedModules = useMemo(() => {
    return moduleUsage.filter((m) => m.isUsed);
  }, [moduleUsage]);

  const unusedModules = useMemo(() => {
    return moduleUsage.filter((m) => !m.isUsed);
  }, [moduleUsage]);

  // User requirement: If usage > 4, show all modules; if <= 4, show only used modules!
  useEffect(() => {
    if (moduleUsage.length > 0) {
      if (usedModules.length > 4) {
        // More than 4 used: select all modules by default
        setSelectedModules(new Set(moduleUsage.map((m) => m.key)));
      } else if (usedModules.length > 0) {
        // 4 or less used: select only used modules by default
        setSelectedModules(new Set(usedModules.map((m) => m.key)));
      } else {
        setSelectedModules(new Set());
      }
    }
  }, [moduleUsage, usedModules]);

  // Toggle single module selection
  const handleToggleModule = (key) => {
    setSelectedModules((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Select all modules
  const handleSelectAll = () => {
    setSelectedModules(new Set(moduleUsage.map((m) => m.key)));
  };

  // Deselect all modules
  const handleDeselectAll = () => {
    setSelectedModules(new Set());
  };

  // Select only modules that are in use
  const handleSelectUsedOnly = () => {
    const usedKeys = usedModules.map((m) => m.key);
    setSelectedModules(new Set(usedKeys));
  };

  // Toggle all used modules
  const handleToggleAllUsed = (checked) => {
    setSelectedModules((prev) => {
      const next = new Set(prev);
      usedModules.forEach((m) => {
        if (checked) {
          next.add(m.key);
        } else {
          next.delete(m.key);
        }
      });
      return next;
    });
  };

  // Toggle all unused modules
  const handleToggleAllUnused = (checked) => {
    setSelectedModules((prev) => {
      const next = new Set(prev);
      unusedModules.forEach((m) => {
        if (checked) {
          next.add(m.key);
        } else {
          next.delete(m.key);
        }
      });
      return next;
    });
  };

  // Checklist assign actions
  const handleAssignOne = async () => {
    if (!selectedItem) return;
    setLoading(true);
    try {
      const res = await postRequest(
        POST_URL.company_checklist_items.api,
        {
          company: company_id,
          checklist_item: selectedItem,
          is_done: false,
        },
        props
      );
      if (res && res.status === 200) {
        Swal.fire("Success", "Checklist item assigned", "success");
        setSelectedItem("");
        fetchData(company_id);
      } else {
        Swal.fire("Error", "Failed to assign checklist item", "error");
      }
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to assign", "error");
    }
    setLoading(false);
  };

  const handleAssignAll = async () => {
    setLoading(true);
    try {
      const res = await postRequest(
        POST_URL.company_checklist_items.api + "assign-all/",
        { company_id: company_id },
        props
      );
      if (res && res.status === 200) {
        Swal.fire("Success", "All checklist items assigned", "success");
        fetchData(company_id);
      } else {
        Swal.fire("Error", "Failed to assign checklist items", "error");
      }
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to assign", "error");
    }
    setLoading(false);
  };

  const handleStatusChange = async (companyItemId, isDone) => {
    try {
      const res = await patchRequest(
        `${PATCH_URL.company_checklist_items.api}${companyItemId}/`,
        { is_done: isDone },
        props
      );
      if (res && res.status === 200) {
        setCompanyItems((prev) =>
          prev.map((ci) =>
            ci.id === companyItemId ? { ...ci, is_done: isDone } : ci
          )
        );
      } else {
        Swal.fire("Error", "Failed to update status", "error");
      }
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to update status", "error");
    }
  };

  // ─── HIGH-QUALITY PARTITIONED PDF GENERATION ───
  const handleDownloadPDF = async () => {
    // If usage > 4: show all modules (both used and unused)
    // If usage <= 4: show only the used modules ("that much only")
    let modulesToInclude = [];
    if (usedModules.length > 4) {
      modulesToInclude = moduleUsage.filter((m) => selectedModules.has(m.key));
    } else {
      modulesToInclude = usedModules.filter((m) => selectedModules.has(m.key));
      // If user also explicitly selected unused modules, include them
      const extraSelected = unusedModules.filter((m) => selectedModules.has(m.key));
      if (showUnused && extraSelected.length > 0) {
        modulesToInclude = [...modulesToInclude, ...extraSelected];
      }
    }

    const selectedUsedList = modulesToInclude.filter((m) => m.isUsed);
    const selectedUnusedList = modulesToInclude.filter((m) => !m.isUsed);

    if (
      selectedUsedList.length === 0 &&
      selectedUnusedList.length === 0 &&
      companyItems.length === 0
    ) {
      Swal.fire(
        "No Content to Generate",
        "Please select at least one module or assign checklist tasks before creating the PDF.",
        "warning"
      );
      return;
    }

    setPdfLoading(true);
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14;

      const schoolName =
        companyInfo?.name ||
        reportData?.institute_name ||
        "Educational Institute";
      const schoolCode = companyInfo?.code || "";
      const schoolDomain = companyInfo?.domain || "";
      const logoUrl = companyInfo?.logo || "";

      // Load logo safely
      let logoBase64 = null;
      if (logoUrl) {
        try {
          logoBase64 = await loadImageAsBase64(logoUrl);
        } catch (err) {
          console.warn("Logo loading skipped:", err);
        }
      }

      // ─── HEADER BANNER (Compact 38mm) ───
      const headerHeight = 38;
      doc.setFillColor(21, 46, 90);
      doc.rect(0, 0, pageWidth, headerHeight, "F");

      // Accent Royal Blue Line
      doc.setFillColor(37, 99, 235);
      doc.rect(0, headerHeight, pageWidth, 2, "F");

      let textStartX = margin;

      if (logoBase64) {
        try {
          doc.setFillColor(255, 255, 255);
          doc.roundedRect(margin, 5, 28, 28, 2, 2, "F");
          doc.addImage(logoBase64, "PNG", margin + 2, 7, 24, 24);
          textStartX = margin + 33;
        } catch (e) {
          textStartX = margin;
        }
      } else {
        doc.setFillColor(30, 58, 110);
        doc.circle(margin + 13, 19, 12, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text(
          schoolName.charAt(0).toUpperCase() || "E",
          margin + 13,
          24,
          { align: "center" }
        );
        textStartX = margin + 30;
      }

      // School Name
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(15);
      doc.setFont("helvetica", "bold");
      doc.text(schoolName, textStartX, 15);

      // School Meta Tags (Code, Domain)
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(219, 234, 254);
      let subText = [];
      if (schoolCode) subText.push(`Code: ${schoolCode}`);
      if (schoolDomain) subText.push(`Domain: ${schoolDomain}`);
      if (subText.length > 0) {
        doc.text(subText.join("   |   "), textStartX, 23);
      }

      // Subtitle
      doc.setFontSize(7.5);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(147, 197, 253);
      doc.text("OFFICIAL MODULE USAGE & VERIFICATION REPORT", textStartX, 31);

      let yPos = 47;

      const todayStr = new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });

      // ─── PARTITION 1: ACTIVE / USED MODULES ───
      // Single Status column: 'Status' (Using)
      if (selectedUsedList.length > 0) {
        doc.setTextColor(16, 120, 56);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(`Active / Used Modules (${selectedUsedList.length})`, margin, yPos);

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 116, 139);
        doc.text(`Generated Date: ${todayStr}`, pageWidth - margin, yPos, {
          align: "right",
        });
        yPos += 3;

        const usedTableData = selectedUsedList.map((mod, idx) => [
          idx + 1,
          mod.label,
          "Using",
          mod.details || "Active",
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [["Sl. No.", "Module / Feature", "Status", "Activity / Details"]],
          body: usedTableData,
          margin: { left: margin, right: margin },
          styles: {
            fontSize: 8,
            cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
            lineWidth: 0.15,
            lineColor: [226, 232, 240],
            textColor: [51, 65, 85],
          },
          headStyles: {
            fillColor: [16, 120, 56],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 8.5,
          },
          columnStyles: {
            0: { cellWidth: 16, halign: "center", fontStyle: "bold" },
            1: { cellWidth: 55, fontStyle: "bold" },
            2: { cellWidth: 30, halign: "center" },
            3: { cellWidth: "auto" },
          },
          didParseCell: function (data) {
            if (data.section === "head" && (data.column.index === 0 || data.column.index === 2)) {
              data.cell.styles.halign = "center";
            }
            if (data.section === "body" && data.column.index === 2) {
              data.cell.styles.textColor = [16, 149, 68];
              data.cell.styles.fontStyle = "bold";
            }
          },
          alternateRowStyles: {
            fillColor: [240, 253, 244],
          },
        });

        yPos = (doc.lastAutoTable && doc.lastAutoTable.finalY) || yPos + 30;
        yPos += 6;
      }

      // ─── PARTITION 2: UNUSED MODULES (SHOWN IF USAGE > 4 OR USER SELECTED) ───
      // Single Status column: 'Status' (Not Using)
      if (selectedUnusedList.length > 0) {
        if (yPos > pageHeight - 50) {
          doc.addPage();
          yPos = 18;
        }

        doc.setTextColor(71, 85, 105);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(`Unused Modules (${selectedUnusedList.length})`, margin, yPos);
        yPos += 3;

        const unusedTableData = selectedUnusedList.map((mod, idx) => [
          idx + 1,
          mod.label,
          "Not Using",
          mod.details || "Never Used",
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [["Sl. No.", "Module / Feature", "Status", "Activity / Details"]],
          body: unusedTableData,
          margin: { left: margin, right: margin },
          styles: {
            fontSize: 8,
            cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
            lineWidth: 0.15,
            lineColor: [226, 232, 240],
            textColor: [51, 65, 85],
          },
          headStyles: {
            fillColor: [71, 85, 105],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 8.5,
          },
          columnStyles: {
            0: { cellWidth: 16, halign: "center", fontStyle: "bold" },
            1: { cellWidth: 55, fontStyle: "bold" },
            2: { cellWidth: 30, halign: "center" },
            3: { cellWidth: "auto" },
          },
          didParseCell: function (data) {
            if (data.section === "head" && (data.column.index === 0 || data.column.index === 2)) {
              data.cell.styles.halign = "center";
            }
            if (data.section === "body" && data.column.index === 2) {
              data.cell.styles.textColor = [220, 38, 38];
              data.cell.styles.fontStyle = "bold";
            }
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252],
          },
        });

        yPos = (doc.lastAutoTable && doc.lastAutoTable.finalY) || yPos + 30;
        yPos += 6;
      }

      // ─── ASSIGNED CHECKLIST TASKS TABLE (SINGLE STATUS COLUMN) ───
      // Shows: Sl. No., Checklist Task, Status (Done / Not Done), Verification / Remarks
      if (companyItems.length > 0) {
        if (yPos > pageHeight - 55) {
          doc.addPage();
          yPos = 18;
        }

        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(15, 23, 42);
        doc.text(`Checklist Tasks (${companyItems.length})`, margin, yPos);
        yPos += 3;

        const checklistTableData = companyItems.map((ci, idx) => {
          let updatedText = "-";
          if (ci.updated_by_name) {
            updatedText = `Updated by ${ci.updated_by_name}`;
            if (ci.modified) {
              updatedText += ` on ${new Date(ci.modified).toLocaleDateString()}`;
            }
          } else if (ci.is_done) {
            updatedText = "Completed";
          } else {
            updatedText = "Pending";
          }

          return [
            idx + 1,
            ci.checklist_item_name || `Item #${ci.checklist_item}`,
            ci.is_done ? "Done" : "Not Done",
            updatedText,
          ];
        });

        autoTable(doc, {
          startY: yPos,
          head: [["Sl. No.", "Checklist Task", "Status", "Verification / Remarks"]],
          body: checklistTableData,
          margin: { left: margin, right: margin },
          styles: {
            fontSize: 8,
            cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
            lineWidth: 0.15,
            lineColor: [226, 232, 240],
            textColor: [51, 65, 85],
          },
          headStyles: {
            fillColor: [21, 46, 90],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 8.5,
          },
          columnStyles: {
            0: { cellWidth: 16, halign: "center", fontStyle: "bold" },
            1: { cellWidth: 55, fontStyle: "bold" },
            2: { cellWidth: 30, halign: "center" },
            3: { cellWidth: "auto" },
          },
          didParseCell: function (data) {
            if (data.section === "head" && (data.column.index === 0 || data.column.index === 2)) {
              data.cell.styles.halign = "center";
            }
            if (data.section === "body" && data.column.index === 2) {
              const isDone = data.cell.raw === "Done";
              data.cell.styles.textColor = isDone
                ? [16, 149, 68]
                : [220, 38, 38];
              data.cell.styles.fontStyle = "bold";
            }
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252],
          },
        });

        yPos = (doc.lastAutoTable && doc.lastAutoTable.finalY) || yPos + 30;
        yPos += 8;
      }

      // Check if verification and signature fit on current page
      const blockHeight = 26;
      if (yPos + blockHeight > pageHeight - 15) {
        doc.addPage();
        yPos = 20;
      }

      // ─── VERIFICATION & PRINCIPAL SIGNATURE BLOCK ───
      const blockY = yPos;

      // Left Box: Audit notice
      doc.setDrawColor(226, 232, 240);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(margin, blockY, 88, 24, 2, 2, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(21, 46, 90);
      doc.text("INSTITUTIONAL AUDIT NOTICE", margin + 4, blockY + 5);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      doc.text("This report reflects authenticated usage data recorded", margin + 4, blockY + 11);
      doc.text("on the Edubricz Campus Management ERP Platform.", margin + 4, blockY + 16);
      doc.text("Official Record - Valid for Compliance & Audit", margin + 4, blockY + 21);

      // Right: Principal Signature
      const sigWidth = 65;
      const sigStartX = pageWidth - margin - sigWidth;

      doc.setDrawColor(148, 163, 184);
      doc.setLineWidth(0.4);
      doc.line(sigStartX, blockY + 14, pageWidth - margin, blockY + 14);

      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 41, 59);
      doc.text("Principal's Signature", sigStartX + sigWidth / 2, blockY + 18, {
        align: "center",
      });

      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text(
        "(Authorized Signatory & Seal)",
        sigStartX + sigWidth / 2,
        blockY + 22,
        { align: "center" }
      );

      // ─── DYNAMIC FOOTER PAGINATION ACROSS ALL PAGES ───
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.3);
        doc.line(margin, pageHeight - 10, pageWidth - margin, pageHeight - 10);

        doc.setFontSize(7);
        doc.setTextColor(148, 163, 184);
        doc.text(
          "Edubricz Campus Management Platform • Confidential System Report",
          margin,
          pageHeight - 6
        );
        doc.text(
          `${schoolName} • Page ${i} of ${totalPages}`,
          pageWidth - margin,
          pageHeight - 6,
          { align: "right" }
        );
      }

      // Save PDF
      const sanitizedName = schoolName.replace(/[^a-zA-Z0-9_-]/g, "_");
      doc.save(`${sanitizedName}_Checklist_Report.pdf`);
    } catch (e) {
      console.error("PDF generation error:", e);
      Swal.fire("Error", "Failed to generate PDF: " + (e.message || ""), "error");
    }
    setPdfLoading(false);
  };

  const isAllUsedSelected =
    usedModules.length > 0 &&
    usedModules.every((m) => selectedModules.has(m.key));
  const isUsedIndeterminate =
    usedModules.some((m) => selectedModules.has(m.key)) && !isAllUsedSelected;

  const isAllUnusedSelected =
    unusedModules.length > 0 &&
    unusedModules.every((m) => selectedModules.has(m.key));
  const isUnusedIndeterminate =
    unusedModules.some((m) => selectedModules.has(m.key)) &&
    !isAllUnusedSelected;

  if (loading && !companyInfo) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: hideAssign ? 0 : 2 }}>
      {/* ─── ASSIGN CHECKLIST TASKS SECTION ─── */}
      {!hideAssign && (
        <Grid container spacing={2} alignItems="center" mb={3}>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Checklist Item</InputLabel>
              <Select
                value={selectedItem}
                label="Select Checklist Item"
                onChange={(e) => setSelectedItem(e.target.value)}
              >
                {unassignedItems.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <Button
              variant="contained"
              onClick={handleAssignOne}
              disabled={!selectedItem || loading}
            >
              Assign
            </Button>
          </Grid>
          <Grid item xs={6} md={4} sx={{ textAlign: "right" }}>
            <Button
              variant="outlined"
              onClick={handleAssignAll}
              disabled={loading || unassignedItems.length === 0}
            >
              Assign All
            </Button>
          </Grid>
        </Grid>
      )}

      {/* ─── MODULE USAGE SECTION HEADER & CONTROLS (ONLY ONE DOWNLOAD BUTTON) ─── */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Module Usage Checklist
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active modules are shown below. Unused modules are hidden by default and can be viewed if needed.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Select all modules">
              <Button
                size="small"
                variant="outlined"
                startIcon={<SelectAllIcon />}
                onClick={handleSelectAll}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Select All
              </Button>
            </Tooltip>
            <Tooltip title="Select only modules currently used">
              <Button
                size="small"
                variant="outlined"
                color="success"
                onClick={handleSelectUsedOnly}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Used Only
              </Button>
            </Tooltip>
            <Tooltip title="Deselect all">
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<DeselectIcon />}
                onClick={handleDeselectAll}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Clear
              </Button>
            </Tooltip>
            {/* ONLY ONE DOWNLOAD BUTTON */}
            <Button
              variant="contained"
              startIcon={
                pdfLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <PictureAsPdfIcon />
                )
              }
              onClick={handleDownloadPDF}
              disabled={pdfLoading}
              sx={{
                background: "linear-gradient(135deg, #1e3a8a, #2563eb)",
                "&:hover": {
                  background: "linear-gradient(135deg, #172554, #1d4ed8)",
                },
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.85rem",
                borderRadius: 2,
                px: 2.5,
                boxShadow: "0 3px 10px rgba(37, 99, 235, 0.3)",
              }}
            >
              {pdfLoading
                ? "Generating..."
                : selectedModules.size > 0
                ? `Download PDF (${selectedModules.size})`
                : "Download PDF"}
            </Button>
          </Box>
        </Box>

        {/* ══════════════════════════════════════════════
            PARTITION 1: ACTIVE / USED MODULES (ALWAYS VISIBLE)
            ══════════════════════════════════════════════ */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
              p: 1.2,
              px: 2,
              borderRadius: 2,
              background: "linear-gradient(90deg, #ecfdf5 0%, #f0fdf4 100%)",
              border: "1px solid #a7f3d0",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TaskAltIcon sx={{ color: "#16a34a", fontSize: 24 }} />
              <Typography variant="subtitle1" fontWeight={700} color="#065f46">
                Active / Used Modules
              </Typography>
              <Chip
                label={`${usedModules.length} Modules`}
                size="small"
                sx={{
                  bgcolor: "#16a34a",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                }}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  color="success"
                  checked={isAllUsedSelected}
                  indeterminate={isUsedIndeterminate}
                  onChange={(e) => handleToggleAllUsed(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" fontWeight={600} color="#065f46">
                  Select All Used
                </Typography>
              }
              sx={{ m: 0 }}
            />
          </Box>

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              borderColor: "#a7f3d0",
              boxShadow: "0 2px 8px rgba(16, 185, 129, 0.06)",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#065f46" }}>
                  <TableCell padding="checkbox" sx={{ color: "#fff", pl: 2 }}>
                    <Checkbox
                      color="primary"
                      checked={isAllUsedSelected}
                      indeterminate={isUsedIndeterminate}
                      onChange={(e) => handleToggleAllUsed(e.target.checked)}
                      sx={{
                        color: "#fff",
                        "&.Mui-checked": { color: "#fff" },
                        "&.MuiCheckbox-indeterminate": { color: "#fff" },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ color: "#fff", fontWeight: 700, fontSize: "0.85rem", width: 80 }}
                  >
                    Sl. No.
                  </TableCell>
                  <TableCell
                    sx={{ color: "#fff", fontWeight: 700, fontSize: "0.85rem", minWidth: 180 }}
                  >
                    Module / Feature
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ color: "#fff", fontWeight: 700, fontSize: "0.85rem", width: 130 }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{ color: "#fff", fontWeight: 700, fontSize: "0.85rem" }}
                  >
                    Activity / Details
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usedModules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3, color: "#64748b" }}>
                      No modules currently detected in use for this school.
                    </TableCell>
                  </TableRow>
                ) : (
                  usedModules.map((mod, idx) => {
                    const isSelected = selectedModules.has(mod.key);
                    return (
                      <TableRow
                        key={mod.key}
                        hover
                        selected={isSelected}
                        onClick={() => handleToggleModule(mod.key)}
                        sx={{
                          cursor: "pointer",
                          background: idx % 2 === 0 ? "#ffffff" : "#f0fdf4",
                          "&:hover": { background: "#dcfce7 !important" },
                        }}
                      >
                        <TableCell padding="checkbox" sx={{ pl: 2 }}>
                          <Checkbox
                            color="success"
                            checked={isSelected}
                            onChange={() => handleToggleModule(mod.key)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: "#065f46" }}>
                          {idx + 1}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color="#0f172a">
                            {mod.label}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={<CheckCircleIcon style={{ color: "#16a34a" }} />}
                            label="Used"
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              bgcolor: "#ecfdf5",
                              color: "#16a34a",
                              border: "1px solid #a7f3d0",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#15803d",
                              fontWeight: 600,
                            }}
                          >
                            {mod.details}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* ══════════════════════════════════════════════
            PARTITION 2: UNUSED MODULES (HIDDEN BY DEFAULT, TOGGLEABLE)
            ══════════════════════════════════════════════ */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
              p: 1.2,
              px: 2,
              borderRadius: 2,
              background: "linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%)",
              border: "1px solid #cbd5e1",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <HighlightOffIcon sx={{ color: "#64748b", fontSize: 24 }} />
              <Typography variant="subtitle1" fontWeight={700} color="#334155">
                Unused Modules
              </Typography>
              <Chip
                label={`${unusedModules.length} Modules`}
                size="small"
                sx={{
                  bgcolor: "#64748b",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {showUnused && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      color="default"
                      checked={isAllUnusedSelected}
                      indeterminate={isUnusedIndeterminate}
                      onChange={(e) => handleToggleAllUnused(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={600} color="#475569">
                      Select All Unused
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              )}

              {/* TOGGLE TO SHOW / HIDE UNUSED MODULES */}
              <Button
                size="small"
                variant={showUnused ? "contained" : "outlined"}
                color="inherit"
                startIcon={showUnused ? <VisibilityOffIcon /> : <VisibilityIcon />}
                onClick={() => setShowUnused(!showUnused)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "#94a3b8",
                  color: showUnused ? "#fff" : "#334155",
                  bgcolor: showUnused ? "#475569" : "transparent",
                  "&:hover": {
                    bgcolor: showUnused ? "#334155" : "rgba(0,0,0,0.04)",
                  },
                }}
              >
                {showUnused
                  ? "Hide Unused Modules"
                  : `Show Unused Modules (${unusedModules.length})`}
              </Button>
            </Box>
          </Box>

          <Collapse in={showUnused}>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                borderColor: "#e2e8f0",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: "#475569" }}>
                    <TableCell padding="checkbox" sx={{ color: "#fff", pl: 2 }}>
                      <Checkbox
                        color="primary"
                        checked={isAllUnusedSelected}
                        indeterminate={isUnusedIndeterminate}
                        onChange={(e) => handleToggleAllUnused(e.target.checked)}
                        sx={{
                          color: "#fff",
                          "&.Mui-checked": { color: "#fff" },
                          "&.MuiCheckbox-indeterminate": { color: "#fff" },
                        }}
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ color: "#fff", fontWeight: 700, fontSize: "0.85rem", width: 80 }}
                    >
                      Sl. No.
                    </TableCell>
                    <TableCell
                      sx={{ color: "#fff", fontWeight: 700, fontSize: "0.85rem", minWidth: 180 }}
                    >
                      Module / Feature
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ color: "#fff", fontWeight: 700, fontSize: "0.85rem", width: 130 }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{ color: "#fff", fontWeight: 700, fontSize: "0.85rem" }}
                    >
                      Activity / Details
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unusedModules.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3, color: "#64748b" }}>
                        All modules are actively in use.
                      </TableCell>
                    </TableRow>
                  ) : (
                    unusedModules.map((mod, idx) => {
                      const isSelected = selectedModules.has(mod.key);
                      return (
                        <TableRow
                          key={mod.key}
                          hover
                          selected={isSelected}
                          onClick={() => handleToggleModule(mod.key)}
                          sx={{
                            cursor: "pointer",
                            background: idx % 2 === 0 ? "#ffffff" : "#f8fafc",
                            "&:hover": { background: "#f1f5f9 !important" },
                          }}
                        >
                          <TableCell padding="checkbox" sx={{ pl: 2 }}>
                            <Checkbox
                              color="default"
                              checked={isSelected}
                              onChange={() => handleToggleModule(mod.key)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, color: "#64748b" }}>
                            {idx + 1}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} color="#1e293b">
                              {mod.label}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              icon={<CancelIcon style={{ color: "#dc2626" }} />}
                              label="Not Used"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                bgcolor: "#fef2f2",
                                color: "#dc2626",
                                border: "1px solid #fecaca",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#94a3b8",
                                fontWeight: 400,
                              }}
                            >
                              {mod.details}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* ─── EXISTING CHECKLIST TASKS SECTION ─── */}
      <Typography variant="h6" mb={2} fontWeight={700}>
        Checklist Tasks
      </Typography>
      {companyItems.length === 0 && (
        <Typography color="textSecondary">
          No checklist items assigned yet.
        </Typography>
      )}

      {companyItems.length > 0 && (
        <Card variant="outlined" sx={{ borderRadius: 2.5, borderColor: "#e2e8f0" }}>
          <CardContent>
            <FormGroup>
              {companyItems.map((ci) => (
                <Box
                  key={ci.id}
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                  >
                    <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 500, color: "#1e293b" }}>
                      {ci.checklist_item_name}
                    </Typography>
                    <RadioGroup
                      row
                      value={ci.is_done ? "yes" : "no"}
                      onChange={(e) =>
                        handleStatusChange(ci.id, e.target.value === "yes")
                      }
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio size="small" color="success" />}
                        label={<Typography variant="body2" fontWeight={500}>Yes</Typography>}
                        sx={{ mr: 2 }}
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio size="small" color="error" />}
                        label={<Typography variant="body2" fontWeight={500}>No</Typography>}
                        sx={{ mr: 0 }}
                      />
                    </RadioGroup>
                  </Box>
                  {ci.updated_by_name && ci.modified && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      sx={{ ml: 1 }}
                    >
                      Updated by {ci.updated_by_name} on{" "}
                      {new Date(ci.modified).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              ))}
            </FormGroup>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default CompanyChecklistTab;
